import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import { Add, Warning } from '@factorialco/f0-react/icons/app'
import { FundaeFinishGroupDatatableQuery } from 'generated/resources/datatable.graphql'
import {
  FundaeFinishGroupFixReferenceContractDocument,
  FundaeFinishGroupUpdatePersonalInfoDocument,
} from 'generated/resources/graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import SpainIdValidationStrategy from 'modules/legal_entities/lib/identifierValidators/spain'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import {
  useDebounced,
  useSyncedState,
} from 'modules/trainings/components/Revamp/TrainingsDetail/Fundae/finalizar-grupo-formativo/content/utils'

type ItemType<T> = T extends (infer U)[] ? U : never
type DataTableItem = ItemType<
  FundaeFinishGroupDatatableQuery['trainings']['fundaeFinishGroupsConnection']['edges']
>['node']
type EditableCell = DataTableItem & { validate?: boolean; trainingId?: string }

export function EducationLevelCell({ access, validate: globalValidate, trainingId }: EditableCell) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [validate, setValidate] = useSyncedState(globalValidate)
  const [value, setValue] = useSyncedState(access?.employee?.referenceContract?.esEducationLevelId)
  const {
    queryResult: { data },
  } = useDataTable<FundaeFinishGroupDatatableQuery>()
  const options = useMemo(
    () =>
      data?.contracts.spanishEducationLevels
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [data]
  )
  const [fixReferenceContract] = useMutation(FundaeFinishGroupFixReferenceContractDocument, {
    extractorFn(queryData) {
      return queryData.contractsMutations.fixContractsContractVersion
    },
  })

  const mappigns = data?.trainings.categoryMapConfigs.find(
    (item) => item.category === 'educationlevel'
  )?.categoryMapConfigItems

  if (value && !isMapped()) {
    return (
      <MissingMapping
        label={String(options?.find((option) => option.value === value)?.label)}
        onClick={() => {
          navigate(
            navigation.trainings.list.details_revamp.view.fundae.educationLevel.fullPath({
              id: String(trainingId),
            })
          )
        }}
      />
    )
  }

  return (
    <Selector.Popover
      blankLabel={i18n.t(
        'trainings.trainings_detail.fundae.participants_datatable.categories_blank_label'
      )}
      error={validation() ? undefined : 'ERROR'}
      hideError
      placeholder={i18n.t('common.none')}
      onToggle={() => {
        if (!validate) setValidate(true)
      }}
      onChange={(newValue) => {
        setValue(Number(newValue))
        fixReferenceContract({
          variables: {
            id: Number(access?.employee?.referenceContract?.id),
            employeeId: Number(access?.employee?.id),
            esEducationLevelId: newValue ? Number(newValue) : null,
          },
        })
      }}
      value={[value]}
    >
      {options.map((item) => {
        return (
          <Selector.Option
            key={value}
            searchValue={item.label}
            value={item.value}
            label={item.label}
          >
            {item.label}
          </Selector.Option>
        )
      })}
    </Selector.Popover>
  )

  function isMapped() {
    return Boolean(mappigns?.find((item) => item.field.fieldId === value))
  }

  function validation() {
    if (!validate) return true
    return Boolean(value)
  }
}

export function ProfessionalCategoryCell({
  access,
  validate: globalValidate,
  trainingId,
}: EditableCell) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [validate, setValidate] = useSyncedState(globalValidate)
  const [value, setValue] = useSyncedState(
    access?.employee?.referenceContract?.esProfessionalCategoryId
  )
  const {
    queryResult: { data },
  } = useDataTable<FundaeFinishGroupDatatableQuery>()

  const options = useMemo(
    () =>
      data?.contracts.spanishProfessionalCategories
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true })
        ) || [],
    [data]
  )

  const [fixReferenceContract] = useMutation(FundaeFinishGroupFixReferenceContractDocument, {
    extractorFn(queryData) {
      return queryData.contractsMutations.fixContractsContractVersion
    },
  })

  const mappigns = data?.trainings.categoryMapConfigs.find(
    (item) => item.category === 'professionalcategory'
  )?.categoryMapConfigItems

  if (value && !isMapped()) {
    return (
      <MissingMapping
        onClick={() => {
          navigate(
            navigation.trainings.list.details_revamp.view.fundae.professionalCategory.fullPath({
              id: String(trainingId),
            })
          )
        }}
        label={String(options?.find((option) => option.value === value)?.label)}
      />
    )
  }

  return (
    <Selector.Popover
      blankLabel={i18n.t(
        'trainings.trainings_detail.fundae.participants_datatable.categories_blank_label'
      )}
      error={validation() ? undefined : 'ERROR'}
      hideError
      onToggle={() => {
        if (!validate) setValidate(true)
      }}
      onChange={(newValue) => {
        setValue(Number(newValue))
        fixReferenceContract({
          variables: {
            id: Number(access?.employee?.referenceContract?.id),
            employeeId: Number(access?.employee?.id),
            esProfessionalCategoryId: newValue ? Number(newValue) : null,
          },
        })
      }}
      placeholder={i18n.t('common.none')}
      value={[value]}
    >
      {options.map((item) => {
        return (
          <Selector.Option
            key={value}
            searchValue={item.label}
            value={item.value}
            label={item.label}
          >
            {item.label}
          </Selector.Option>
        )
      })}
    </Selector.Popover>
  )

  function validation() {
    if (!validate) return true
    return Boolean(value)
  }

  function isMapped() {
    return Boolean(mappigns?.find((item) => item.field.fieldId === value))
  }
}

export function NifCell({ access, validate: globalValidate }: EditableCell) {
  const [validate, setValidate] = useSyncedState(globalValidate)
  const [value, setValue] = useSyncedState(access?.employee?.identifier || '')
  const [updatePersonalInfo] = useMutation(FundaeFinishGroupUpdatePersonalInfoDocument, {
    extractorFn(data) {
      return data.employeesMutations.updateEmployeesEmployee
    },
  })
  const debouncedUpdatePersonalInfo = useDebounced(updatePersonalInfo)

  return (
    <TextField
      error={validation(value) ? undefined : 'ERROR'}
      hideError
      onBlur={() => {
        if (!validate) setValidate(true)
      }}
      // We're NOT communicating async validation here yet
      // The F.E. format validaiton works mostly, but
      // our B.E. validates duplicates on identifiers
      // which we do not.
      onChange={(newValue) => {
        setValue(newValue)
        if (validation(newValue))
          debouncedUpdatePersonalInfo({
            variables: {
              id: Number(access?.employee?.id),
              identifier: newValue,
              identifierType: identifierType(newValue),
            },
          })
      }}
      value={value}
    />
  )

  function validation(identifier: string) {
    if (!validate) return true

    return new SpainIdValidationStrategy(
      identifierType(identifier),
      identifier,
      identifierType(identifier)
    ).isValid()
  }
}

// We validate only DNI because this is only for Spain and the only doc types
// accepted for FUNDAE are DNI or NIE
const DNI_REGEX = /^\d{8}[A-Z]$/
function identifierType(identifier: string): SpainIdType {
  if (DNI_REGEX.test(identifier)) {
    return SpainIdType.DNI
  }
  return SpainIdType.NIE
}

export function PhoneCell({ access, validate: globalValidate }: EditableCell) {
  const [validate, setValidate] = useSyncedState(globalValidate)
  const [value, setValue] = useSyncedState(access?.employee?.phoneNumber || '')
  const [updatePersonalInfo] = useMutation(FundaeFinishGroupUpdatePersonalInfoDocument, {
    extractorFn(data) {
      return data.employeesMutations.updateEmployeesEmployee
    },
  })
  const debouncedUpdatePersonalInfo = useDebounced(updatePersonalInfo)

  return (
    <TextField
      error={validation(value) ? undefined : 'MISSING'}
      hideError
      onBlur={() => {
        if (!validate) setValidate(true)
      }}
      maxLength={15}
      min={5}
      onChange={(newValue) => {
        setValue(newValue)
        debouncedUpdatePersonalInfo({
          variables: {
            id: Number(access?.employee?.id),
            phoneNumber: newValue ? newValue : null,
          },
        })
      }}
      type="number"
      value={value}
    />
  )

  // TODO: review this
  // This is an internation standard on phone sizes? Should we be more strict?
  // F! right now is hyper loose on this
  function validation(phoneNumber: string) {
    if (!validate) return true
    return phoneNumber.length >= 5 && phoneNumber.length <= 15
  }
}

function MissingMapping({ label, onClick }: { label: string; onClick(): void }) {
  return (
    <>
      <Box flexDirection="row" alignItems="center" gap="s8" justifyContent="spaceBetween">
        <F0Icon icon={Warning} className="text-f1-icon-critical" />
        <Text size="300" ellipsis>
          {label}
        </Text>
        <Box className="ml-auto">
          <F0Button onClick={onClick} icon={Add} label="Add mapping" hideLabel variant="outline" />
        </Box>
      </Box>
    </>
  )
}
